// src/portal-app/tabs/MoreTab.js
//
// P3 — grouped iOS list. More is genuinely a settings menu, so it is the one
// tab that stays pure navigation; every other landing now carries live data.
// The old 2x2 grid is gone (spec §1 complaint 5).
import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useNav } from '../nav/NavStack';
import { useHubCards } from '../nav/useHubCards';
import {
    GroupedRows, ensureKitCss,
    IconPlus, IconAlert, IconMessage, IconEdit, IconPhone, IconSearch, IconMore, IconFilter,
} from '../shell/kit';
import { ensureTabCss } from './tabCss';
import { HubEmptyState } from './HubEmptyState';

// Groups CONSECUTIVE cards sharing a sectionKey, matching the old
// HubScreen.chunkBySection. Non-consecutive matches deliberately form
// separate groups — order in tabs.js is the author's intent.
function chunkBySection(cards) {
    const out = [];
    cards.forEach((card) => {
        const last = out[out.length - 1];
        if (last && last.sectionKey === card.sectionKey) last.cards.push(card);
        else out.push({ sectionKey: card.sectionKey, sectionFallback: card.sectionFallback, cards: [card] });
    });
    return out;
}

// Per-card icon, chosen where a semantic fit exists in the kit's small set;
// IconPlus (catalogue-building) is the neutral fallback for anything else.
const ICON_MAP = {
    products: IconPlus,     // building the menu
    inventory: IconAlert,   // stock-level warnings
    marketing: IconMessage, // campaigns / customer messaging
    settings: IconEdit,     // editing configuration
    devices: IconPhone,     // registered hardware (tills/tablets/phones)
    help: IconSearch,       // searching help articles
    account: IconMore,      // personal-account overflow menu
    roles: IconFilter,      // permission tiers
};

const LockIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    </svg>
);

// Maps one gated card (from useHubCards) to a GroupedRows row. `testId`
// lands on the row's real element (the actual <button> or <div> — see
// GroupedRows.js) as `data-testid="grouped-row-${testId}"`, so the tappable
// vs non-tappable assertions in MoreTab.test.js read the genuine tag.
function buildRow(card, t, push) {
    const locked = card.state === 'locked';
    const tappable = card.state === 'active';
    const Icon = locked ? LockIcon : (ICON_MAP[card.id] || IconPlus);

    return {
        id: card.id,
        testId: card.id,
        icon: <Icon />,
        label: t(card.labelKey, {}, card.fallback),
        value: locked ? (
            <span className="tab-locked-hint">{t('hub_locked_hint', {}, 'Upgrade to unlock')}</span>
        ) : undefined,
        onPress: tappable ? () => push(card.id) : undefined,
    };
}

export default function MoreTab() {
    ensureKitCss();
    ensureTabCss();
    const { t } = useLanguage();
    const { push } = useNav();
    const cards = useHubCards('more');

    return (
        <div className="tab-wrap">
            <h1 className="tab-title">{t('tab_more', {}, 'More')}</h1>
            {cards.length === 0 ? (
                <HubEmptyState t={t} testId="more-empty" />
            ) : chunkBySection(cards).map((chunk, i) => (
                <React.Fragment key={`grp-${i}`}>
                    {chunk.sectionKey ? (
                        <p className="pk-section-label">{t(chunk.sectionKey, {}, chunk.sectionFallback)}</p>
                    ) : null}
                    <div className="tab-group">
                        <GroupedRows rows={chunk.cards.map((card) => buildRow(card, t, push))} />
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
}
